import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import {
  Loader2,
  Play,
  MapPin,
  Star,
  Phone,
  Globe,
  Locate,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PlacesTester() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    latitude: 37.7749,
    longitude: -122.4194,
    category: "restaurant",
    radius_km: 5,
    max_results: 15,
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/categories`);
      setCats(data);
    })();
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) =>
        setForm((f) => ({
          ...f,
          latitude: +p.coords.latitude.toFixed(6),
          longitude: +p.coords.longitude.toFixed(6),
        })),
      () => toast.error("Location denied")
    );
  };

  const run = async () => {
    setLoading(true);
    setError(null);
    setResults([]);
    try {
      const cat = cats.find((c) => c.key === form.category);
      const included_types = cat?.types || ["restaurant"];
      const { data } = await api.post("/places/nearby", {
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        included_types,
        radius: Number(form.radius_km) * 1000,
        max_results: Number(form.max_results),
      });
      setResults(data);
      toast.success(`Found ${data.length} place${data.length === 1 ? "" : "s"}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail
          ? JSON.stringify(detail)
          : "Request failed";
      setError(msg);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="mb-8 animate-in-up">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
          Diagnostic
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
          Places API Tester
        </h1>
        <p className="text-sm text-muted-foreground max-w-xl">
          Fire a live nearby-search against Google Places (New) via the backend proxy.
          Use this to validate your API key and see exactly what the mobile app receives.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px,1fr] gap-6">
        {/* Form */}
        <div className="bg-card border border-border rounded-2xl p-6 h-fit lg:sticky lg:top-4">
          <div className="text-sm font-bold mb-5">Query</div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Latitude
                </Label>
                <Input
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={(e) =>
                    setForm({ ...form, latitude: e.target.value })
                  }
                  data-testid="tester-lat-input"
                  className="mt-1.5 h-11 mono text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Longitude
                </Label>
                <Input
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={(e) =>
                    setForm({ ...form, longitude: e.target.value })
                  }
                  data-testid="tester-lng-input"
                  className="mt-1.5 h-11 mono text-sm"
                />
              </div>
            </div>

            <Button
              variant="outline"
              onClick={useMyLocation}
              className="w-full h-10"
              data-testid="tester-my-location-btn"
            >
              <Locate className="w-4 h-4 mr-2" /> Use my location
            </Button>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Category
              </Label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="tester-category-select"
                className="mt-1.5 w-full h-11 rounded-lg border border-input bg-background px-3 text-sm font-medium"
              >
                {cats.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Radius (km)
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={form.radius_km}
                  onChange={(e) =>
                    setForm({ ...form, radius_km: e.target.value })
                  }
                  data-testid="tester-radius-input"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Max results
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={form.max_results}
                  onChange={(e) =>
                    setForm({ ...form, max_results: e.target.value })
                  }
                  data-testid="tester-max-input"
                  className="mt-1.5 h-11"
                />
              </div>
            </div>

            <Button
              onClick={run}
              disabled={loading}
              className="w-full h-12 text-base font-semibold"
              data-testid="tester-run-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Run nearby search
            </Button>
          </div>
        </div>

        {/* Results */}
        <div>
          {error && (
            <div
              data-testid="tester-error"
              className="bg-destructive/5 border border-destructive/30 rounded-2xl p-5 mb-4"
            >
              <div className="text-sm font-bold text-destructive mb-1">
                Request failed
              </div>
              <div className="mono text-xs text-destructive/90 break-all">{error}</div>
            </div>
          )}

          {loading && (
            <div className="text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}

          {!loading && results.length === 0 && !error && (
            <div className="bg-card border border-border rounded-2xl p-16 text-center">
              <MapPin className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
              <div className="text-sm font-medium">Run a search to see results</div>
              <div className="text-xs text-muted-foreground mt-1">
                Results will appear here.
              </div>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-3 stagger" data-testid="tester-results">
              {results.map((p) => (
                <div
                  key={p.id}
                  className="bg-card border border-border rounded-2xl p-5 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-base font-bold truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {p.address || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-extrabold text-primary leading-none">
                        {p.distance_km?.toFixed(2)}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                        km
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-3">
                    {p.rating != null && (
                      <span className="inline-flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                        <span className="font-semibold text-foreground">
                          {p.rating.toFixed(1)}
                        </span>
                        <span>({p.user_rating_count ?? 0})</span>
                      </span>
                    )}
                    {p.primary_type && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-secondary text-secondary-foreground font-medium">
                        {p.primary_type}
                      </span>
                    )}
                    {p.open_now != null && (
                      <span
                        className={`inline-flex items-center gap-1 font-semibold ${
                          p.open_now ? "text-emerald-600" : "text-muted-foreground"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            p.open_now ? "bg-emerald-500" : "bg-muted-foreground"
                          }`}
                        />
                        {p.open_now ? "Open now" : "Closed"}
                      </span>
                    )}
                    {p.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" /> {p.phone}
                      </span>
                    )}
                    {p.website && (
                      <a
                        href={p.website}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <Globe className="w-3.5 h-3.5" /> Website
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
